import { EChartsOption } from 'echarts'
import ReactEcharts from 'echarts-for-react'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { getFormattedFinance } from 'partner360/helpers/getFormattedFinance'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import { TRANSLATION_KEYS } from '../../translations'

import { ONE_MILLION } from './config'
import { convertToMillion } from './helpers/convertToMillion'
import { IFinancialsChartProps } from './models/financialsChartProps'

export const FinancialsChart: FC<IFinancialsChartProps> = ({
  partnersPrevFYearActuals,
  partnersCurrentFYearActuals,
  partnersCurrentFYearTargets,
  globalPartnerCurrentFYearTargets,
  rollupQuotas,
  fYear,
  valueType,
}) => {
  const t = useSafetyTranslation()
  const translationMode = useSelector(selectTranslationMode)

  const measure = !valueType || valueType === 'Ndp' ? 'NDP' : valueType

  const currentFYearDigits = useMemo(() => fYear.toString().slice(-2), [fYear])
  const previousFYearDigits = useMemo(() => (fYear - 1).toString().slice(-2), [fYear])

  const option = useMemo<EChartsOption>(() => {
    const chartTitle = getTranslatedText({
      t,
      key: TRANSLATION_KEYS.globalPartnerExpanded.financialsChart.chartTitle,
      replaceParams: [{ key: '{valueType}', value: measure }],
    })

    return {
      title: {
        subtext: chartTitle,
        subtextStyle: {
          fontSize: 16,
        },
      },
      tooltip: {
        trigger: 'item',
        valueFormatter: (value) => {
          return `$ ${getFormattedFinance((value as number) * ONE_MILLION)}`
        },
      },
      xAxis: {
        type: 'category',
        data: [
          `FY${previousFYearDigits} Actuals`,
          `FY${currentFYearDigits} Quotas`,
          `FY${currentFYearDigits} GP Targets`,
          `FY${currentFYearDigits} Targets Roll-up`,
          `FY${currentFYearDigits} Actuals`,
        ],
        axisLabel: { interval: 0, rotate: 30 },
      },
      yAxis: {
        type: 'value',
      },
      grid: {
        left: '15%',
        right: '5%',
      },
      series: [
        {
          type: 'bar',
          barWidth: '40px',
          name: '',
          data: [
            {
              value: convertToMillion(Math.round(partnersPrevFYearActuals)),
              itemStyle: { color: '#009f9b' },
            },
            {
              value: convertToMillion(Math.round(rollupQuotas)),
              itemStyle: { color: '#6015a1' },
            },
            {
              value: convertToMillion(Math.round(globalPartnerCurrentFYearTargets)),
              itemStyle: { color: '#fc1a1a' },
            },
            {
              value: convertToMillion(Math.round(partnersCurrentFYearTargets)),
              itemStyle: { color: '#fc6020' },
            },
            {
              value: convertToMillion(Math.round(partnersCurrentFYearActuals)),
              itemStyle: { color: '#009f9b' },
            },
          ],
        },
      ],
    }
  }, [
    t,
    measure,
    partnersPrevFYearActuals,
    partnersCurrentFYearActuals,
    partnersCurrentFYearTargets,
    globalPartnerCurrentFYearTargets,
    rollupQuotas,
    currentFYearDigits,
    previousFYearDigits,
  ])

  if (translationMode === 'edit') {
    return (
      <div>
        <Translation
          text={TRANSLATION_KEYS.globalPartnerExpanded.financialsChart.chartTitle}
          replaceParams={[{ key: '{valueType}', value: measure }]}
        />
      </div>
    )
  }

  return <ReactEcharts option={option} opts={{ height: 300 }} />
}
